import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CalendarPlus, ExternalLink, MapPin, Video } from 'lucide-react';
import { useMeetings } from '../hooks/useApi';
import MeetingModal from '../components/MeetingModal';
import {
    Avatar,
    Badge,
    Button,
    Card,
    DataTable,
    EmptyState,
    PageHeader,
} from '../components/ui';

const Meetings = () => {
    const navigate = useNavigate();
    const { data: meetingsData, isLoading } = useMeetings();
    const [isMeetingModalOpen, setIsMeetingModalOpen] = useState(false);

    const meetings = meetingsData?.data || [];

    const columns = [
        {
            key: 'title',
            header: 'Meeting',
            render: (m) => (
                <Link to={`/meetings/${m.id}`} onClick={(e) => e.stopPropagation()} className="group block">
                    <span className="block font-medium text-ink-900 group-hover:text-primary-600 transition-colors duration-150 truncate max-w-[320px]">
                        {m.title}
                    </span>
                </Link>
            ),
        },
        {
            key: 'meeting_date',
            header: 'Date & time',
            render: (m) => (
                <>
                    <span className="block text-ink-800 whitespace-nowrap">
                        {new Date(`${m.meeting_date}T12:00:00`).toLocaleDateString(undefined, {
                            weekday: 'short',
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                        })}
                    </span>
                    <span className="block text-micro text-ink-500 tabular">
                        {m.start_time?.slice(0, 5)} – {m.end_time?.slice(0, 5)}
                    </span>
                </>
            ),
        },
        {
            key: 'meeting_type',
            header: 'Type',
            render: (m) => (
                <Badge variant={m.meeting_type === 'virtual' ? 'info' : 'neutral'}>
                    {m.meeting_type === 'virtual' ? 'Virtual' : 'Face to face'}
                </Badge>
            ),
        },
        {
            key: 'venue',
            header: 'Venue / link',
            render: (m) =>
                m.meeting_type === 'virtual' ? (
                    m.meeting_link ? (
                        <a
                            href={m.meeting_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 text-secondary-600 hover:text-secondary-700 transition-colors duration-150 truncate max-w-[200px]"
                        >
                            <Video size={13} aria-hidden="true" className="shrink-0" />
                            <span className="truncate">Join meeting</span>
                            <ExternalLink size={11} aria-hidden="true" className="shrink-0" />
                        </a>
                    ) : (
                        <span className="text-muted">No link provided</span>
                    )
                ) : (
                    <span className="inline-flex items-center gap-1.5 text-ink-700">
                        <MapPin size={13} className="text-ink-400 shrink-0" aria-hidden="true" />
                        <span className="truncate max-w-[180px]">{m.venue || 'To be confirmed'}</span>
                    </span>
                ),
        },
        {
            key: 'created_by',
            header: 'Registered by',
            render: (m) => (
                <span className="flex items-center gap-2">
                    <Avatar name={`${m.first_name || ''} ${m.last_name || ''}`} size="sm" />
                    <span className="text-ink-700 truncate max-w-[130px]">
                        {m.first_name} {m.last_name}
                    </span>
                </span>
            ),
        },
        {
            key: 'view',
            header: '',
            align: 'right',
            width: 100,
            render: (m) => (
                <Link
                    to={`/meetings/${m.id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="btn btn-ghost btn-sm"
                >
                    View
                </Link>
            ),
        },
    ];

    return (
        <>
            <PageHeader
                title="ICT Meetings"
                description="Scheduled sessions, their location and who registered them."
                breadcrumbs={[{ label: 'Home', to: '/' }, { label: 'Meetings' }]}
                actions={
                    <Button variant="primary" icon={CalendarPlus} onClick={() => setIsMeetingModalOpen(true)}>
                        Register meeting
                    </Button>
                }
            />

            <Card>
                <DataTable
                    columns={columns}
                    rows={meetings}
                    loading={isLoading}
                    onRowClick={(m) => navigate(`/meetings/${m.id}`)}
                    empty={
                        <EmptyState
                            icon={CalendarPlus}
                            title="No meetings registered"
                            description="Register a meeting to have it appear here and on the shared calendar."
                            action={
                                <Button variant="primary" icon={CalendarPlus} onClick={() => setIsMeetingModalOpen(true)}>
                                    Register meeting
                                </Button>
                            }
                        />
                    }
                />
            </Card>

            <MeetingModal isOpen={isMeetingModalOpen} onClose={() => setIsMeetingModalOpen(false)} />
        </>
    );
};

export default Meetings;
